import React from 'react';

import { Tariff, TariffFormValues, Product } from 'types/state';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { useServices } from 'hooks/useServices';
import { useForm } from 'hooks/useForm';
import { setCustomTimeout } from 'utils/setCustomTimeout';
import { DONE_TIMEOUT_DURATION } from 'utils/constants';
import { useServiceState } from 'hooks/useServiceState';
import { createTariffFormValidator } from 'utils/tariffFormUtils';
import { DateControl } from 'design/dateControl';
import { normalizeDate } from 'utils/normalizeDate';
import { useIntlCode } from 'hooks/useIntlCode';
import { TariffUpdateError } from 'utils/errors';
import { SidebarModalSectionComponent } from 'design/sidebarModal';
import { TextComponent } from 'design/text/textComponents';
import { FormControlComponent } from 'design/formControl';
import { FieldsetLayoutComponent } from 'design/fieldsetLayout';
import { ModalPressControlComponent } from 'design/modalPressControl';
import { MarginComponent } from 'design/margin';
import { FloatingLabelComponent } from 'design/floatingLabel';
import { TextInputControlComponent } from 'design/textInputControl';

type Props = {
  product: Product;
  tariff?: Tariff;
  lastTariff?: Tariff;
  onCancel(): void;
  onSubmitDidSucceed(): void;
};

export const ProductManagerTariffEditor = (props: Props) => {
  const {
    product,
    tariff = null,
    lastTariff = null,
    onCancel,
    onSubmitDidSucceed,
  } = props;

  const intl = useIntlDictionary();

  const intlCode = useIntlCode();

  const { editTariff: editTariffService } = useServices();

  const tomorrowServerDate = React.useMemo(() => {
    const date = new Date();

    date.setDate(date.getDate() + 1);

    return normalizeDate(date).toServerDateString(intlCode);
  }, [intlCode]);

  const twoDaysAfterLastTarifExpiresAtServerDate = React.useMemo(() => {
    if (lastTariff) {
      const date = new Date(lastTariff.expiresAt);

      date.setDate(date.getDate() + 2);

      return normalizeDate(date).toServerDateString(intlCode);
    }

    return null;
  }, [intlCode, lastTariff]);

  const initialValues = React.useMemo<TariffFormValues>(() => {
    if (tariff) {
      return {
        cost: `${tariff.cost}`,
        expiresAt: tariff.expiresAt,
      };
    }

    if (twoDaysAfterLastTarifExpiresAtServerDate) {
      return {
        cost: '',
        expiresAt: twoDaysAfterLastTarifExpiresAtServerDate,
      };
    }

    return {
      cost: '',
      expiresAt: tomorrowServerDate,
    };
  }, [tariff, tomorrowServerDate, twoDaysAfterLastTarifExpiresAtServerDate]);

  const validator = React.useMemo(() => createTariffFormValidator(intl), [
    intl,
  ]);

  const { values, setValues, validation, validate } = useForm({
    initialValues,
    validator,
  });

  const [editTariffState, editTariff] = useServiceState(editTariffService, {
    throwError: false,
  });

  React.useEffect(() => {
    if (editTariffState && editTariffState.resolved) {
      return setCustomTimeout(() => {
        onSubmitDidSucceed();
      }, DONE_TIMEOUT_DURATION);
    }
  }, [values, editTariffState, onSubmitDidSucceed]);

  const tariffUpdateFailed = (() => {
    return (
      editTariffState &&
      editTariffState.rejectedWith instanceof TariffUpdateError
    );
  })();

  const onSubmit = React.useCallback(() => {
    validate(() => {
      editTariff({ product, tariff, formValues: values });
    });
  }, [validate, editTariff, product, tariff, values]);

  const handleDateControlValueChange = React.useCallback(
    (nextValue: string) => {
      setValues((prevValues) => ({
        ...prevValues,
        expiresAt: nextValue,
      }));
    },
    [setValues]
  );

  const onCostChange = React.useCallback(
    (nextValue: string) => {
      setValues((prevValues) => ({
        ...prevValues,
        cost: nextValue,
      }));
    },
    [setValues]
  );

  const minimumExpiresAt = tomorrowServerDate;

  const titleText =
    tariff === null
      ? intl.productDetails.tariffEditorCreateTitle
      : intl.productDetails.tariffEditorUpdateTitle;

  const hasViolations = !!validation.cost || !!validation.expiresAt;

  const costHint = tariffUpdateFailed
    ? intl.inputValidationTariffUpdateIsForbidden
    : validation.cost;

  const costIsInvalid = tariffUpdateFailed || !!validation.cost;

  return (
    <>
      <SidebarModalSectionComponent
        theme="head"
        content={
          <TextComponent size="mpp" weight="medium">
            {titleText}
          </TextComponent>
        }
        actions={null}
      />
      <FormControlComponent
        shouldFocusFirstInvalidInput={hasViolations}
        onSubmit={onSubmit}
      >
        <SidebarModalSectionComponent
          content={
            <FieldsetLayoutComponent>
              <FloatingLabelComponent
                lifted={!!values.cost}
                invalid={costIsInvalid}
                titleText={
                  <TextComponent color="secondary" size="m">
                    {intl.costInputPlaceholder}
                  </TextComponent>
                }
                hintText={
                  <TextComponent color="critical" size="s">
                    {costHint}
                  </TextComponent>
                }
              >
                <TextInputControlComponent
                  name="cost"
                  autoFocus
                  value={values.cost}
                  invalid={costIsInvalid}
                  onValueChange={onCostChange}
                />
              </FloatingLabelComponent>
              <FloatingLabelComponent
                lifted={!!values.expiresAt}
                invalid={!!validation.expiresAt}
                titleText={
                  <TextComponent color="secondary" size="m">
                    {intl.expiresAtInputPlaceholder}
                  </TextComponent>
                }
                hintText={
                  <TextComponent color="critical" size="s">
                    {validation.expiresAt}
                  </TextComponent>
                }
              >
                <DateControl
                  value={values.expiresAt}
                  minimumDate={minimumExpiresAt}
                  onValueChange={handleDateControlValueChange}
                  placeholder={intl.expiresAtInputPlaceholder}
                />
              </FloatingLabelComponent>
            </FieldsetLayoutComponent>
          }
          actions={
            <>
              <ModalPressControlComponent
                disabled={editTariffState?.pending}
                theme="primary"
                type="submit"
              >
                {intl.saveChangesButton}
              </ModalPressControlComponent>
              <MarginComponent top={20} />
              <ModalPressControlComponent
                disabled={editTariffState?.pending}
                theme="secondary"
                type="button"
                onPress={onCancel}
              >
                {intl.cancelButton}
              </ModalPressControlComponent>
            </>
          }
        />
      </FormControlComponent>
    </>
  );
};
